// ── Welle K6: Note-Vorschläge aus unentschuldigtem Fehlen — Hinweis-Text und Fenster ──
// Am PC steht über dem Board nur eine ruhige Zeile; „Prüfen“ öffnet diese Liste im K4-Fenster
// (Note wählen, Bestätigen, Verwerfen). Die Aufrufe macht HeuteScreen (dieselben wie am Handy).
import React from 'react';
import { LdSheet } from '../../../components/ui/LdSheet';
import { LdAuswahl } from '../../../components/ld/LdFenster';
import type { NoteVorschlagDash } from '../HeuteScreen';

/** „2026-W43“ → „KW 43“ */
export function kwText(schulWoche: string) {
  const m = /W(\d{1,2})$/.exec(schulWoche ?? '');
  return m ? `KW ${Number(m[1])}` : schulWoche;
}

const und = (teile: string[]) => teile.length <= 1 ? (teile[0] ?? '') : `${teile.slice(0, -1).join(', ')} und ${teile[teile.length - 1]}`;

/** Satz der Hinweis-Zeile, z. B. „aus unentschuldigtem Fehlen, z. B. Jonas Weber und Noah Becker (Deutsch, KW 43)“ */
export function noteVorschlagText(liste: NoteVorschlagDash[]) {
  const namen = Array.from(new Set(liste.map(v => `${v.vorname} ${v.nachname}`)));
  const faecher = Array.from(new Set(liste.map(v => v.fach)));
  const wochen = Array.from(new Set(liste.map(v => v.schul_woche))).sort();
  const wer = namen.length === 1 ? `von ${namen[0]}` : `z. B. ${und(namen.slice(0, 2))}`;
  const fach = faecher.length <= 2 ? und(faecher) : `${faecher.length} Fächer`;
  const kw = wochen.length === 1 ? kwText(wochen[0]) : `${kwText(wochen[0])} bis ${kwText(wochen[wochen.length - 1]).replace('KW ', '')}`;
  return `aus unentschuldigtem Fehlen, ${wer} (${fach}, ${kw})`;
}

const ttmm = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.`;

type Props = {
  open: boolean;
  onClose: () => void;
  vorschlaege: NoteVorschlagDash[];
  noten: Record<number, number>;
  setNote: (id: number, note: number) => void;
  onBestaetigen: (v: NoteVorschlagDash) => void;
  onVerwerfen: (v: NoteVorschlagDash) => void;
};

export function NoteVorschlaegeFenster({ open, onClose, vorschlaege, noten, setNote, onBestaetigen, onVerwerfen }: Props) {
  const n = vorschlaege.length;
  return (
    <LdSheet open={open} onClose={onClose} maxWidth={600}
      kopf={{ titel: 'Note-Vorschläge prüfen', icon: 'grading', kontext: n ? `${n} ${n === 1 ? 'Vorschlag' : 'Vorschläge'} aus unentschuldigtem Fehlen: Note wählen, dann bestätigen oder verwerfen.` : null }}>
      {n === 0 ? (
        <p className="ld-n6__leer">Alle Vorschläge sind erledigt.</p>
      ) : (
        <div className="ld-n6">
          {vorschlaege.map(v => (
            <div key={v.id} className="ld-n6__zeile">
              <div className="ld-n6__wer">
                <b>{v.vorname} {v.nachname}</b>
                <span>{v.fach}, {kwText(v.schul_woche)}, {v.vorfaelle_anzahl}× unentschuldigt{v.datums?.length ? ` (${v.datums.map(ttmm).join(', ')})` : ''}</span>
              </div>
              <LdAuswahl style={{ width: 76, minHeight: 40 }} aria-label={`Note für ${v.vorname} ${v.nachname}`}
                value={noten[v.id] ?? v.vorgeschlagene_note} onChange={e => setNote(v.id, Number(e.target.value))}>
                {[1, 2, 3, 4, 5, 6].map(x => <option key={x} value={x}>{x}</option>)}
              </LdAuswahl>
              <button type="button" className="ld-n6__knopf" onClick={() => onBestaetigen(v)}>Bestätigen</button>
              <button type="button" className="ld-n6__neben" onClick={() => onVerwerfen(v)}>Verwerfen</button>
            </div>
          ))}
        </div>
      )}
    </LdSheet>
  );
}
